import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@/auth/auth-context';
import { fetchDoctorEvidenceInbox, type DoctorEvidenceInbox } from '@/dashboard/api';
import type { DashboardMe } from '@/dashboard/types';
import { authBearer, safeErrorMessage } from '@/lib/portal-api';

export interface DoctorPendingState {
  readonly identity: string;
  readonly data: DoctorEvidenceInbox | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refresh: () => Promise<boolean>;
}
const PendingContext = createContext<DoctorPendingState | null>(null);
export const useDoctorPending = () => useContext(PendingContext);

/** One scoped read supplies the Pending page and its navigation badge. A new
 * actor/scope remounts the owner; same-actor token refresh retains open capture. */
export function DoctorPendingProvider({ me, children }: { me: DashboardMe | null; children: ReactNode }) {
  const { session, loading } = useAuth();
  if (loading || !session || me?.role !== 'doctor') return <>{children}</>;
  const identity = JSON.stringify([session.user.id, me.organizationId, [...me.clinicIds].sort((a,b)=>a-b)]);
  return <PendingOwner key={identity} identity={identity} bearer={session.access_token}>{children}</PendingOwner>;
}
function PendingOwner({ identity, bearer, children }: { identity: string; bearer: string; children: ReactNode }) {
  const [data,setData] = useState<DoctorEvidenceInbox | null>(null);
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState<string | null>(null);
  const sequence = useRef(0), live = useRef(true), latestBearer = useRef(bearer);
  latestBearer.current = bearer;
  const refresh = useCallback(async () => {
    const request = ++sequence.current, token = latestBearer.current;
    setLoading(true); setError(null);
    try {
      const value = await fetchDoctorEvidenceInbox(token);
      const currentBearer=await authBearer();
      if (!live.current || sequence.current !== request || latestBearer.current !== token) return false;
      if (currentBearer !== token) throw new Error('The session changed. Try again.');
      if (!Array.isArray(value.pendingVisits)) throw new Error('The visit list is unavailable.');
      setData(value); return true;
    } catch (err) {
      if (live.current && sequence.current === request && latestBearer.current === token) {
        // No stale names/counts or capture behind a refused/failed scoped read.
        setData(null); setError(safeErrorMessage(err, 'Pending visits could not load. Try again.'));
      }
      return false;
    } finally { if (live.current && sequence.current === request && latestBearer.current === token) setLoading(false); }
  }, []);
  useEffect(() => { live.current=true; return () => { live.current=false; sequence.current++; }; }, []);
  useEffect(() => { void refresh(); }, [bearer, refresh]);
  useEffect(() => { const focus=()=>{void refresh();}; window.addEventListener('focus',focus); return()=>window.removeEventListener('focus',focus); }, [refresh]);
  return <PendingContext.Provider value={{identity,data,loading,error,refresh}}>{children}</PendingContext.Provider>;
}
export function DoctorPendingBadge() {
  const pending=useDoctorPending();
  // The current reader covers drafts and evidence, not every finalized missing
  // answer/consultation-consent obligation. Never certify a complete zero.
  const count=pending?.data?.pendingVisits?.length;
  if (!pending || pending.loading || pending.error || !count) return null;
  return <span className="doctor-pending__nav-count" aria-label={`At least ${count} pending visits`}>{count}+</span>;
}
